import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { fetchEcheancesDocuments } from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Ce qui arrive à terme dans la bibliothèque.
 *
 * Une assurance expirée ne prévient pas. Elle ne bloque rien, ne fait rien
 * échouer, n'apparaît nulle part — et une buvette non assurée un soir de fête
 * engage des personnes, pas une structure. C'est exactement le genre d'oubli
 * qu'un logiciel doit porter à la place de quelqu'un.
 *
 * Le bandeau est posé sur l'accès rapide plutôt que sur un tableau de bord :
 * c'est l'écran qu'on ouvre en arrivant, et une alerte qu'il faut aller
 * chercher n'est pas une alerte.
 *
 * Il ne paraît que s'il a quelque chose à dire. Un encadré permanent qui
 * annonce que tout va bien apprend à ne plus être lu, et le jour où il change
 * de couleur, plus personne ne le regarde.
 */
export default function AlerteEcheances() {
  const { aLeDroit } = useAuth();

  const { data } = useQuery({
    queryKey: ['documents-echeances'],
    queryFn: fetchEcheancesDocuments,
    enabled: aLeDroit('voir_documents'),
    // Une échéance bouge à l'échelle du mois : la relire à chaque montage
    // de l'écran serait du bruit réseau pour rien.
    staleTime: 60 * 60 * 1000,
  });

  const documents = (data?.documents ?? []).filter(
    (d) => d.joursAvantEcheance !== null && d.joursAvantEcheance <= 60
  );

  if (documents.length === 0) return null;

  return (
    <div className="card bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
      <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        {documents.length === 1 ? 'Un document arrive à terme' : `${documents.length} documents arrivent à terme`}
      </p>
      <ul className="text-sm text-amber-900 dark:text-amber-200 space-y-0.5">
        {documents.slice(0, 4).map((d) => {
          const jours = d.joursAvantEcheance ?? 0;
          return (
            <li key={d.id}>
              <strong>{d.titre}</strong> —{' '}
              {jours < 0
                ? `expiré depuis ${Math.abs(jours)} jour${Math.abs(jours) > 1 ? 's' : ''}`
                : jours === 0
                  ? "expire aujourd'hui"
                  : `expire dans ${jours} jour${jours > 1 ? 's' : ''}`}
            </li>
          );
        })}
        {documents.length > 4 && (
          <li className="text-xs">… et {documents.length - 4} autre(s).</li>
        )}
      </ul>
      <Link
        to="/bureau/documents"
        className="inline-block text-sm font-medium text-amber-900 dark:text-amber-200 underline"
      >
        Ouvrir la bibliothèque
      </Link>
    </div>
  );
}
